import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  FaWallet,
  FaDownload,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaFilter,
  FaEdit,
  FaCheckCircle,
  FaBan,
  FaSearch,
  FaExchangeAlt,
  FaChartBar,
  FaChartPie,
  FaChartLine,
  FaTrash,
  FaPaperclip,
  FaFile,
  FaFilePdf,
  FaFileImage,
  FaFileExcel,
  FaFileWord,
  FaExternalLinkAlt,
  FaMoneyBillWave,
  FaHourglassHalf,
  FaTimesCircle,
  FaArrowCircleDown,
  FaFileUpload
} from 'react-icons/fa';
import { collection, getDocs, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import * as XLSX from 'xlsx';
import styles from './BudgetManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableFilter from '../../components/TableFilter';
import AddIncomeModal from './AddIncomeModal';
import AddExpenseModal from './AddExpenseModal';
import ImportIncomeExcelModal from './ImportIncomeExcelModal';
import ImportExpenseExcelModal from './ImportExpenseExcelModal';
import EditIncomeModal from './EditIncomeModal';
import EditExpenseModal from './EditExpenseModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import './BudgetManagementDarkMode.css';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const truncateText = (text, limit = 30) => {
  if (!text) return '-';
  const str = String(text);
  return str.length > limit ? `${str.substring(0, limit)}...` : str;
};

const fmtAmount = (val) => {
  const n = parseFloat(val);
  if (!n || isNaN(n)) return '-';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtInt = (val) => {
  const n = parseFloat(val);
  if (!n || isNaN(n)) return '-';
  return n.toLocaleString('tr-TR');
};

// Display value with fallback chain: e.g. MusteriUnvan || BütçeKaynak
const pick = (row, ...keys) => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return '';
};

const getAttachmentIcon = (fileName) => {
  const ext = fileName.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return <FaFileImage />;
  if (ext === 'pdf') return <FaFilePdf />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <FaFileExcel />;
  if (['doc', 'docx'].includes(ext)) return <FaFileWord />;
  return <FaFile />;
};

// --- Updated Helper Function for Statistics ---
const calculateStatistics = (budgetsData) => {
  let pendingExpense = 0;
  let paidExpense = 0;
  let receivedIncome = 0;
  let pendingIncome = 0;

  // Filter for active items first
  const activeBudgets = budgetsData.filter(budget => budget.AktifDurum !== 'Pasif');

  activeBudgets.forEach(budget => {
    const amount = parseFloat(budget['BütçeMiktarı'] || 0);
    
    if (budget['GiderTipi'] === 'Gelir') {
      // Income Calculation
      if (budget['ÖdemeDurumu'] === 'Alındı') {
        receivedIncome += amount;
      } else if (budget['ÖdemeDurumu'] === 'Beklemede') {
        pendingIncome += amount;
      }
    } else {
      // Expense Calculation
      if (budget['ÖdemeDurumu'] === 'Ödendi') {
        paidExpense += amount;
      } else if (budget['ÖdemeDurumu'] === 'Beklemede') {
        pendingExpense += amount;
      }
    }
  });

  return { 
    activeTotalRecords: activeBudgets.length, // Count only active records
    pendingExpense,
    paidExpense,
    receivedIncome,
    pendingIncome
  };
};
// --- End Updated Helper Function ---

const StatisticsSection = React.memo(({ budgets, isDarkMode }) => {
  // Calculate data for charts
  const calculateChartData = () => {
    const incomeData = budgets.filter(b => b['GiderTipi'] === 'Gelir');
    const expenseData = budgets.filter(b => b['GiderTipi'] !== 'Gelir');

    // Income vs Expense Bar Chart
    const totalIncome = incomeData.reduce((sum, item) => sum + parseFloat(item['BütçeMiktarı'] || 0), 0);
    const totalExpense = expenseData.reduce((sum, item) => sum + parseFloat(item['BütçeMiktarı'] || 0), 0);

    // Payment Status Distribution
    const paymentStatusData = {
      'Alındı': incomeData.filter(i => i['ÖdemeDurumu'] === 'Alındı').length,
      'Beklemede': incomeData.filter(i => i['ÖdemeDurumu'] === 'Beklemede').length,
      'Ödendi': expenseData.filter(e => e['ÖdemeDurumu'] === 'Ödendi').length,
      'Bekleyen': expenseData.filter(e => e['ÖdemeDurumu'] === 'Beklemede').length
    };

    // Category Distribution
    const categoryData = {};
    [...incomeData, ...expenseData].forEach(item => {
      const category = item['BütçeKategorisi'] || 'Diğer';
      categoryData[category] = (categoryData[category] || 0) + 1;
    });

    // Monthly Trend
    const monthlyData = {};
    [...incomeData, ...expenseData].forEach(item => {
      const date = new Date(item['BütçeTarihi']);
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;
      if (!monthlyData[monthYear]) {
        monthlyData[monthYear] = { income: 0, expense: 0 };
      }
      if (item['GiderTipi'] === 'Gelir') {
        monthlyData[monthYear].income += parseFloat(item['BütçeMiktarı'] || 0);
      } else {
        monthlyData[monthYear].expense += parseFloat(item['BütçeMiktarı'] || 0);
      }
    });

    return {
      incomeExpenseData: {
        labels: ['Gelir', 'Gider'],
        datasets: [{
          label: 'Toplam Miktar (₺)',
          data: [totalIncome, totalExpense],
          backgroundColor: ['#38B2AC', '#E53E3E'],
          borderColor: ['#2C7A7B', '#C53030'],
          borderWidth: 1
        }]
      },
      paymentStatusData: {
        labels: Object.keys(paymentStatusData),
        datasets: [{
          data: Object.values(paymentStatusData),
          backgroundColor: ['#38A169', '#ECC94B', '#4299E1', '#ED8936'],
          borderColor: ['#2F855A', '#D69E2E', '#3182CE', '#DD6B20'],
          borderWidth: 1
        }]
      },
      categoryData: {
        labels: Object.keys(categoryData),
        datasets: [{
          data: Object.values(categoryData),
          backgroundColor: [
            '#4299E1', '#48BB78', '#ECC94B', '#ED8936', '#F56565',
            '#9F7AEA', '#4FD1C5', '#F6AD55', '#FC8181', '#B794F4'
          ],
          borderWidth: 1
        }]
      },
      monthlyTrendData: {
        labels: Object.keys(monthlyData),
        datasets: [
          {
            label: 'Gelir',
            data: Object.values(monthlyData).map(d => d.income),
            borderColor: '#38B2AC',
            backgroundColor: 'rgba(56, 178, 172, 0.1)',
            tension: 0.4
          },
          {
            label: 'Gider',
            data: Object.values(monthlyData).map(d => d.expense),
            borderColor: '#E53E3E',
            backgroundColor: 'rgba(229, 62, 62, 0.1)',
            tension: 0.4
          }
        ]
      }
    };
  };

  const chartData = calculateChartData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: isDarkMode ? '#E2E8F0' : '#4A5568'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: isDarkMode ? '#E2E8F0' : '#4A5568'
        },
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }
      },
      x: {
        ticks: {
          color: isDarkMode ? '#E2E8F0' : '#4A5568'
        },
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }
      }
    }
  };

  return (
    <div className={styles.statisticsSection}>
      <h2 className={styles.statisticsTitle}>
        <FaChartBar /> İstatistikler
      </h2>
      <div className={styles.chartsGrid}>
        <div className={styles.chartCard}>
          <h3>Gelir ve Gider Dağılımı</h3>
          <div className={styles.chartContainer}>
            <Bar data={chartData.incomeExpenseData} options={chartOptions} />
          </div>
        </div>
        <div className={styles.chartCard}>
          <h3>Ödeme Durumu</h3>
          <div className={styles.chartContainer}>
            <Pie data={chartData.paymentStatusData} options={chartOptions} />
          </div>
        </div>
        <div className={styles.chartCard}>
          <h3>Kategori Dağılımı</h3>
          <div className={styles.chartContainer}>
            <Pie data={chartData.categoryData} options={chartOptions} />
          </div>
        </div>
        <div className={styles.chartCard}>
          <h3>Aylık Trend</h3>
          <div className={styles.chartContainer}>
            <Line data={chartData.monthlyTrendData} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
});

const BudgetManagementPage = () => {
  const { isDarkMode } = useTheme();
  const { userAuthorization } = useUserProfile();
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin');
  const [budgets, setBudgets] = useState([]);
  const [activeTab, setActiveTab] = useState('gelir');
  const [filteredIncome, setFilteredIncome] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [showLeftScrollIncome, setShowLeftScrollIncome] = useState(false);
  const [showRightScrollIncome, setShowRightScrollIncome] = useState(false);
  const [showLeftScrollExpense, setShowLeftScrollExpense] = useState(false);
  const [showRightScrollExpense, setShowRightScrollExpense] = useState(false);
  const [isAddIncomeModalOpen, setIsAddIncomeModalOpen] = useState(false);
  const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false);
  const [isImportIncomeModalOpen, setIsImportIncomeModalOpen] = useState(false);
  const [isImportExpenseModalOpen, setIsImportExpenseModalOpen] = useState(false);
  const [isEditIncomeModalOpen, setIsEditIncomeModalOpen] = useState(false);
  const [isEditExpenseModalOpen, setIsEditExpenseModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const incomeTableContainerRef = useRef(null);
  const expenseTableContainerRef = useRef(null);
  const filtersRef = useRef(null);
  const [employees, setEmployees] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  
  const [activeTotalRecords, setActiveTotalRecords] = useState(0);
  const [pendingBudgetAmount, setPendingBudgetAmount] = useState(0);
  const [paidBudgetAmount, setPaidBudgetAmount] = useState(0);
  const [receivedIncomeAmount, setReceivedIncomeAmount] = useState(0);
  const [pendingIncomeAmount, setPendingIncomeAmount] = useState(0);

  const fetchBudgets = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Budgets'));
      const budgetsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Use helper function to calculate stats
      const stats = calculateStatistics(budgetsData);
      setActiveTotalRecords(stats.activeTotalRecords);
      setPendingBudgetAmount(stats.pendingExpense);
      setPaidBudgetAmount(stats.paidExpense);
      setReceivedIncomeAmount(stats.receivedIncome);
      setPendingIncomeAmount(stats.pendingIncome);
      
      setBudgets(budgetsData);
      
    } catch (error) {
      console.error('Error fetching budgets:', error);
      toast.error('Bütçeler yüklenirken bir hata oluştu');
    }
  };

  const fetchEmployees = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Employees'));
      const employeesData = {};
      querySnapshot.docs.forEach(doc => {
        employeesData[doc.id] = doc.data();
      });
      setEmployees(employeesData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Çalışanlar yüklenirken bir hata oluştu');
    }
  };

  useEffect(() => {
    fetchBudgets();
    fetchEmployees();
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  const { derivedIncome, derivedExpenses } = useMemo(() => {
    let filtered = budgets;

    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      const searchFields = [
        'BütçeAdı', 'BütçeKaynak', 'BütçeSahibi', 'GiderTipi',
        'GibNumarasi', 'Ettn', 'ZarfUuid', 'Senaryo', 'FaturaTipi',
        'MusteriUnvan', 'MusteriTcknVkn', 'MusteriEtiketi',
        'GondericiUnvan', 'GondericiTcknVkn', 'GondericiEtiketi',
      ];
      filtered = filtered.filter(budget =>
        searchFields.some(f => budget[f] && String(budget[f]).toLowerCase().includes(lowercasedSearch))
      );
    }

    // Some displayed columns fall back to another field when the primary is empty.
    const FILTER_FALLBACKS = {
      MusteriUnvan: ['MusteriUnvan', 'BütçeKaynak'],
      GondericiUnvan: ['GondericiUnvan', 'BütçeKaynak'],
      BelgeTarihi: ['BelgeTarihi', 'BütçeTarihi'],
      Tutar: ['Tutar', 'BütçeMiktarı'],
    };

    Object.keys(filters).forEach(key => {
      const filterValue = filters[key];
      if (filterValue === '' || filterValue === null || filterValue === undefined) return;
      const sourceKeys = FILTER_FALLBACKS[key] || [key];
      if (key === 'ÖdemeDurumu' || key === 'ParaBirimi' || key === 'Senaryo' ||
          key === 'FaturaTipi' || key === 'ZarfDurumu' || key === 'MusteriUnvan' ||
          key === 'GondericiUnvan') {
        filtered = filtered.filter(budget => {
          const v = sourceKeys.map(k => budget[k]).find(x => x !== undefined && x !== null && x !== '');
          return v === filterValue;
        });
      } else {
        const needle = String(filterValue).toLowerCase();
        filtered = filtered.filter(budget => {
          const v = sourceKeys.map(k => budget[k]).find(x => x !== undefined && x !== null && x !== '');
          return v != null && String(v).toLowerCase().includes(needle);
        });
      }
    });

    if (startDateFilter) {
      filtered = filtered.filter(budget => budget['BütçeTarihi'] && budget['BütçeTarihi'] >= startDateFilter);
    }
    if (endDateFilter) {
      filtered = filtered.filter(budget => budget['BütçeTarihi'] && budget['BütçeTarihi'] <= endDateFilter);
    }

    return {
      derivedIncome: filtered.filter(b => b['GiderTipi'] === 'Gelir'),
      derivedExpenses: filtered.filter(b => b['GiderTipi'] !== 'Gelir'),
    };
  }, [budgets, filters, searchTerm, startDateFilter, endDateFilter]);

  useEffect(() => {
    setFilteredIncome(derivedIncome);
    setFilteredExpenses(derivedExpenses);
    if (selectedBudget && !derivedIncome.find(b => b.id === selectedBudget.id) && !derivedExpenses.find(b => b.id === selectedBudget.id)) {
      setSelectedBudget(null);
    }
  }, [derivedIncome, derivedExpenses]); // selectedBudget intentionally excluded — checking presence only

  const handleFilter = (column, value) => {
    const newFilters = { ...filters };
    if (value === '' || value === null || value === undefined) {
        delete newFilters[column];
    } else {
        newFilters[column] = value;
    }
    setFilters(newFilters);
    setSelectedBudget(null);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setStartDateFilter('');
    setEndDateFilter('');
    setSelectedBudget(null);
    setShowFilters(false);
  };

  const handleScroll = (tableType) => { 
    const ref = tableType === 'income' ? incomeTableContainerRef : expenseTableContainerRef;
    const setShowLeft = tableType === 'income' ? setShowLeftScrollIncome : setShowLeftScrollExpense;
    const setShowRight = tableType === 'income' ? setShowRightScrollIncome : setShowRightScrollExpense;

    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      setShowLeft(scrollLeft > 1); 
      setShowRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollTable = (direction, tableType) => {
    const ref = tableType === 'income' ? incomeTableContainerRef : expenseTableContainerRef;
    if (ref.current) {
      const scrollAmount = 300;
      const newScrollLeft = ref.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
      ref.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };
  
  useEffect(() => {
      const incomeContainer = incomeTableContainerRef.current;
      const expenseContainer = expenseTableContainerRef.current;
      
      const incomeScrollHandler = () => handleScroll('income');
      const expenseScrollHandler = () => handleScroll('expense');

      if (incomeContainer) {
          incomeContainer.addEventListener('scroll', incomeScrollHandler);
      }
      if (expenseContainer) {
          expenseContainer.addEventListener('scroll', expenseScrollHandler);
      }

      handleScroll('income');
      handleScroll('expense');

      const resizeObserver = new ResizeObserver(() => {
          handleScroll('income');
          handleScroll('expense');
      });
      if(incomeContainer) resizeObserver.observe(incomeContainer);
      if(expenseContainer) resizeObserver.observe(expenseContainer);
      
      return () => {
          if (incomeContainer) {
              incomeContainer.removeEventListener('scroll', incomeScrollHandler);
              resizeObserver.unobserve(incomeContainer);
          }
          if (expenseContainer) {
              expenseContainer.removeEventListener('scroll', expenseScrollHandler);
               resizeObserver.unobserve(expenseContainer);
          }
      };
  }, [filteredIncome, filteredExpenses]);

  const downloadExcel = () => {
    try {
      const combinedData = [...filteredIncome, ...filteredExpenses];
      
      if (combinedData.length === 0) {
          toast.warn("Dışa aktarılacak veri yok.");
          return;
      }

      const dataToExport = combinedData.map(budget => {
        const isIncome = budget['GiderTipi'] === 'Gelir';
        return {
          'Tip': isIncome ? 'Gelir' : 'Gider',
          'GİB NUMARASI': budget['GibNumarasi'] || '',
          'TASLAK NO': budget['TaslakNo'] || '',
          'SENARYO': budget['Senaryo'] || '',
          'FATURA TİPİ': budget['FaturaTipi'] || '',
          'OLUŞTURMA TARİHİ': budget['OlusturmaTarihi'] || '',
          'OLUŞTURMA ZAMANI': budget['OlusturmaZamani'] || '',
          'BELGE TARİHİ': budget['BelgeTarihi'] || budget['BütçeTarihi'] || '',
          'BELGE ZAMANI': budget['BelgeZamani'] || '',
          'TCKN/VKN': isIncome ? (budget['MusteriTcknVkn'] || '') : (budget['GondericiTcknVkn'] || ''),
          'UNVAN': isIncome ? (budget['MusteriUnvan'] || budget['BütçeKaynak'] || '') : (budget['GondericiUnvan'] || budget['BütçeKaynak'] || ''),
          'ETİKET': isIncome ? (budget['MusteriEtiketi'] || '') : (budget['GondericiEtiketi'] || ''),
          'TUTAR': budget['Tutar'] ?? budget['BütçeMiktarı'] ?? '',
          'PARA BİRİMİ': budget['ParaBirimi'] || '',
          'ZARF UUID': budget['ZarfUuid'] || '',
          'ZARF DURUMU': budget['ZarfDurumu'] || '',
          'ETTN': budget['Ettn'] || '',
          'İSKONTO': budget['Iskonto'] || 0,
          'FATURA DIŞI MASRAFLARI': budget['FaturaDisiMasraflari'] || 0,
          'SATIR SAYISI': budget['SatirSayisi'] || 0,
          'BİRİM FİYAT * MİKTAR TUTARI': budget['BirimFiyatMiktarTutari'] || 0,
          'ÖDENECEK TUTAR': budget['OdenecekTutar'] || 0,
          'VERGİLER HARİÇ TUTAR': budget['VergilerHaricTutar'] || 0,
          'VERGİLER DAHİL TUTAR': budget['VergilerDahilTutar'] || 0,
          'ÖDEME DURUMU': budget['ÖdemeDurumu'] || ''
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'GelirGider');
      XLSX.writeFile(workbook, 'GelirGiderListesi.xlsx');
      
      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Excel dosyası indirilirken bir hata oluştu');
    }
  };

  const handleAddItem = async (itemData) => { 
    try {
      console.log('New item data:', itemData);
      setIsAddIncomeModalOpen(false);
      setIsAddExpenseModalOpen(false);
      await fetchBudgets();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Öğe eklenirken bir hata oluştu');
    }
  };
  
  const handleBudgetSelect = (budget) => {
      if (selectedBudget && selectedBudget.id === budget.id) {
          setSelectedBudget(null);
      } else {
    setSelectedBudget(budget);
      }
  };
  
  const handleToggleStatus = async () => {
      if (!selectedBudget) return;

      const currentStatus = selectedBudget['ÖdemeDurumu'];
      const isIncome = selectedBudget['GiderTipi'] === 'Gelir';
      let newStatus = '';

      if (isIncome) {
          if (currentStatus === 'Alındı') newStatus = 'Beklemede';
          else if (currentStatus === 'Beklemede') newStatus = 'Alındı';
      } else {
          if (currentStatus === 'Ödendi') newStatus = 'Beklemede';
          else if (currentStatus === 'Beklemede') newStatus = 'Ödendi';
      }

      if (!newStatus) {
          toast.warn('Bu durum değiştirilemez.');
          return;
      }

      const budgetRef = doc(db, 'Budgets', selectedBudget.id);
      
      try {
          await updateDoc(budgetRef, {
              ÖdemeDurumu: newStatus
          });
          toast.success(`Durum "${newStatus}" olarak güncellendi.`);
          const updatedBudgets = budgets.map(b => 
              b.id === selectedBudget.id ? { ...b, ÖdemeDurumu: newStatus } : b
          );
          setBudgets(updatedBudgets);

          setSelectedBudget({ ...selectedBudget, ÖdemeDurumu: newStatus });

          const updatedStats = calculateStatistics(updatedBudgets);
          setActiveTotalRecords(updatedStats.activeTotalRecords);
          setPendingBudgetAmount(updatedStats.pendingExpense);
          setPaidBudgetAmount(updatedStats.paidExpense);
          setReceivedIncomeAmount(updatedStats.receivedIncome);
          setPendingIncomeAmount(updatedStats.pendingIncome);

      } catch (error) {
          console.error("Error updating status: ", error);
          toast.error("Durum güncellenirken bir hata oluştu.");
      }
  };
  
  const handleEditBudget = () => {
    if (!selectedBudget) {
      toast.warn("Lütfen düzenlemek için bir öğe seçin.");
      return;
    }
    setEditingBudget(selectedBudget);

    if (selectedBudget['GiderTipi'] === 'Gelir') {
      setIsEditIncomeModalOpen(true);
    } else {
      setIsEditExpenseModalOpen(true);
    }
  };
  
  const handleDeleteBudget = () => {
    if (!selectedBudget) return;
    setDeleteConfirm(true);
  };

  const confirmDeleteAll = async () => {
    setIsDeletingAll(true);
    try {
      const snap = await getDocs(collection(db, 'Budgets'));
      const docs = snap.docs;
      if (!docs.length) {
        toast.info('Silinecek kayıt yok.');
        setDeleteAllConfirm(false);
        setIsDeletingAll(false);
        return;
      }
      const chunkSize = 400;
      for (let i = 0; i < docs.length; i += chunkSize) {
        const batch = writeBatch(db);
        docs.slice(i, i + chunkSize).forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
      toast.success(`${docs.length} kayıt silindi.`);
      setBudgets([]);
      setSelectedBudget(null);
      setActiveTotalRecords(0);
      setPendingBudgetAmount(0);
      setPaidBudgetAmount(0);
      setReceivedIncomeAmount(0);
      setPendingIncomeAmount(0);
      setDeleteAllConfirm(false);
    } catch (err) {
      console.error('Delete all error:', err);
      toast.error('Tüm kayıtlar silinirken bir hata oluştu.');
    } finally {
      setIsDeletingAll(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedBudget) return;
    try {
      await deleteDoc(doc(db, 'Budgets', selectedBudget.id));
      const updatedBudgets = budgets.filter(b => b.id !== selectedBudget.id);
      setBudgets(updatedBudgets);
      setSelectedBudget(null);
      setDeleteConfirm(false);
      const updatedStats = calculateStatistics(updatedBudgets);
      setActiveTotalRecords(updatedStats.activeTotalRecords);
      setPendingBudgetAmount(updatedStats.pendingExpense);
      setPaidBudgetAmount(updatedStats.paidExpense);
      setReceivedIncomeAmount(updatedStats.receivedIncome);
      setPendingIncomeAmount(updatedStats.pendingIncome);
      toast.success('Kayıt başarıyla silindi.');
    } catch (error) {
      console.error('Error deleting budget:', error);
      toast.error('Kayıt silinirken bir hata oluştu.');
    }
  };
  
  const handleUpdateBudget = (budgetId, updatedData) => {
      const updatedBudgets = budgets.map(b => 
          b.id === budgetId ? { ...b, ...updatedData, id: budgetId } : b
      );
      setBudgets(updatedBudgets);

      if (selectedBudget && selectedBudget.id === budgetId) {
          setSelectedBudget({ ...selectedBudget, ...updatedData, id: budgetId });
      }

      const updatedStats = calculateStatistics(updatedBudgets);
      setActiveTotalRecords(updatedStats.activeTotalRecords);
      setPendingBudgetAmount(updatedStats.pendingExpense);
      setPaidBudgetAmount(updatedStats.paidExpense);
      setReceivedIncomeAmount(updatedStats.receivedIncome);
      setPendingIncomeAmount(updatedStats.pendingIncome);
  };

  const handleToggleActiveStatus = async () => {
    if (!selectedBudget) return;

    const currentActiveStatus = selectedBudget.AktifDurum === 'Pasif' ? 'Pasif' : 'Aktif'; 
    const newActiveStatus = currentActiveStatus === 'Aktif' ? 'Pasif' : 'Aktif';

    const budgetRef = doc(db, 'Budgets', selectedBudget.id);

    try {
        await updateDoc(budgetRef, {
            AktifDurum: newActiveStatus
        });
        toast.success(`Öğe "${newActiveStatus}" olarak işaretlendi.`);

        const updatedBudgets = budgets.map(b => 
            b.id === selectedBudget.id ? { ...b, AktifDurum: newActiveStatus } : b
        );
        setBudgets(updatedBudgets);

        setSelectedBudget({ ...selectedBudget, AktifDurum: newActiveStatus });

        const updatedStats = calculateStatistics(updatedBudgets);
        setActiveTotalRecords(updatedStats.activeTotalRecords);
        setPendingBudgetAmount(updatedStats.pendingExpense);
        setPaidBudgetAmount(updatedStats.paidExpense);
        setReceivedIncomeAmount(updatedStats.receivedIncome);
        setPendingIncomeAmount(updatedStats.pendingIncome);

    } catch (error) {
        console.error("Error updating active status: ", error);
        toast.error("Aktif durum güncellenirken bir hata oluştu.");
    }
  };

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);
  
  const canToggleStatus = selectedBudget && 
      ((selectedBudget['GiderTipi'] === 'Gelir' && (selectedBudget['ÖdemeDurumu'] === 'Alındı' || selectedBudget['ÖdemeDurumu'] === 'Beklemede')) ||
       (selectedBudget['GiderTipi'] !== 'Gelir' && (selectedBudget['ÖdemeDurumu'] === 'Ödendi' || selectedBudget['ÖdemeDurumu'] === 'Beklemede')));

  const isAnyFilterActive = searchTerm || Object.values(filters).some(v => v !== '') || startDateFilter || endDateFilter;

  // Determine active status for the selected budget (defaulting to Aktif)
  const isSelectedBudgetActive = !(selectedBudget?.AktifDurum === 'Pasif');

  const uniq = (arr, key) =>
    Array.from(new Set(arr.map(i => i[key]).filter(v => v !== undefined && v !== null && v !== ''))).map(v => ({ value: v, label: String(v) }));

  const incomeUniqueValues = useMemo(() => ({
    Senaryo: uniq(filteredIncome, 'Senaryo'),
    FaturaTipi: uniq(filteredIncome, 'FaturaTipi'),
    MusteriUnvan: uniq(filteredIncome.map(r => ({ ...r, MusteriUnvan: pick(r, 'MusteriUnvan', 'BütçeKaynak') })), 'MusteriUnvan'),
    ParaBirimi: uniq(filteredIncome, 'ParaBirimi'),
    ZarfDurumu: uniq(filteredIncome, 'ZarfDurumu'),
    ÖdemeDurumu: uniq(filteredIncome, 'ÖdemeDurumu'),
  }), [filteredIncome]);

  const expenseUniqueValues = useMemo(() => ({
    Senaryo: uniq(filteredExpenses, 'Senaryo'),
    FaturaTipi: uniq(filteredExpenses, 'FaturaTipi'),
    GondericiUnvan: uniq(filteredExpenses.map(r => ({ ...r, GondericiUnvan: pick(r, 'GondericiUnvan', 'BütçeKaynak') })), 'GondericiUnvan'),
    ParaBirimi: uniq(filteredExpenses, 'ParaBirimi'),
    ZarfDurumu: uniq(filteredExpenses, 'ZarfDurumu'),
    ÖdemeDurumu: uniq(filteredExpenses, 'ÖdemeDurumu'),
  }), [filteredExpenses]);

  // Add function to get employee name
  const getEmployeeName = (employeeId) => {
    if (!employeeId || !employees[employeeId]) return employeeId;
    const employee = employees[employeeId];
    return `${employee.eName} ${employee.eSurname}`;
  };

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <div>
              <h1 className={styles.title}>
                 <FaWallet className={styles.headerIcon} /> Gelir & Gider Yönetimi
              </h1>
               <div className={styles.breadcrumb}>Budget Menu / Income & Expense Management</div>
            </div>
             <div className={`${styles.headerButtons} ${styles.addButtonsContainer}`}>
               <button
                 className={styles.addButton} 
                 onClick={() => setIsAddIncomeModalOpen(true)} 
               >
                 <FaPlus /> Yeni Gelir Ekle
               </button>
              <button
                className={styles.addButton}
                 onClick={() => setIsAddExpenseModalOpen(true)}
              >
                 <FaPlus /> Yeni Gider Ekle
              </button>
              <button
                className={styles.addButton}
                onClick={() => setIsImportIncomeModalOpen(true)}
                title="Gelen E-Fatura Excel dosyasından gelirleri içe aktar"
              >
                <FaFileUpload /> Gelir Excel Yükle
              </button>
              <button
                className={styles.addButton}
                onClick={() => setIsImportExpenseModalOpen(true)}
                title="Giden E-Fatura Excel dosyasından giderleri içe aktar"
              >
                <FaFileUpload /> Gider Excel Yükle
              </button>
              <button
                className={styles.downloadButton}
                onClick={downloadExcel}
              >
                <FaDownload /> Tabloyu İndir
              </button>
              <button
                onClick={() => setDeleteAllConfirm(true)}
                title="TEST: Tüm gelir ve gider kayıtlarını sil"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', border: '1px dashed #E53E3E',
                  background: '#FFF5F5', color: '#C53030',
                  borderRadius: 6, cursor: 'pointer', fontWeight: 600
                }}
              >
                <FaTrash /> Tümünü Sil (Test)
              </button>
            </div>
          </div>
        </header>

        <div className={styles.cardContainer}>
          {/* 1. Received Income */}
          <div className={`${styles.card} ${styles.cardReceivedIncome}`}>
            <div className={styles.cardContent}>
              <div className={styles.cardTextContent}>
                <div className={styles.cardTitle}>Alınan Gelir (Aktif)</div>
                <div className={styles.cardValue}>{receivedIncomeAmount.toLocaleString()} ₺</div>
              </div>
              <div className={`${styles.cardIcon} ${styles.cardIconTeal}`}><FaArrowCircleDown /></div>
            </div>
          </div>

          {/* 2. Pending Income */}
          <div className={`${styles.card} ${styles.cardPendingIncome}`}>
            <div className={styles.cardContent}>
              <div className={styles.cardTextContent}>
                <div className={styles.cardTitle}>Bekleyen Gelir (Aktif)</div>
                <div className={styles.cardValue}>{pendingIncomeAmount.toLocaleString()} ₺</div>
              </div>
              <div className={`${styles.cardIcon} ${styles.cardIconPurple}`}><FaHourglassHalf /></div>
            </div>
          </div>

          {/* 3. Paid Expense */}
          <div className={`${styles.card} ${styles.cardPaid}`}>
            <div className={styles.cardContent}>
              <div className={styles.cardTextContent}>
                <div className={styles.cardTitle}>Tamamlanan Ödeme (Aktif Gider)</div>
                <div className={styles.cardValue}>{paidBudgetAmount.toLocaleString()} ₺</div>
              </div>
              <div className={`${styles.cardIcon} ${styles.cardIconGreen}`}><FaCheckCircle /></div>
            </div>
          </div>

          {/* 4. Pending Expense */}
          <div className={`${styles.card} ${styles.cardPending}`}>
            <div className={styles.cardContent}>
              <div className={styles.cardTextContent}>
                <div className={styles.cardTitle}>Bekleyen Ödeme (Aktif Gider)</div>
                <div className={styles.cardValue}>{pendingBudgetAmount.toLocaleString()} ₺</div>
              </div>
              <div className={`${styles.cardIcon} ${styles.cardIconOrange}`}><FaMoneyBillWave /></div>
            </div>
          </div>
        </div>
        
        <div className={styles.controlsContainer}>
        <div className={styles.searchFilterContainer}>
          <div className={styles.searchBar}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
                placeholder="Unvan, GİB No, ETTN, TCKN/VKN vb. ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button 
                className={styles.clearSearch} 
                onClick={() => setSearchTerm('')}
                  title="Aramayı Temizle"
              >
                <FaTimes />
              </button>
            )}
          </div>
          
          <div className={styles.filterContainer} ref={filtersRef}>
            <button 
              className={styles.filterButton}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className={styles.filterIcon} />
              <span>Filtreler</span>
                {isAnyFilterActive && (
                  <span className={styles.filterBadge}>Aktif</span> 
                )}
              </button>
              
              {showFilters && (
                <div className={styles.dateFilterContainer}>
                  <div className={styles.dateInputGroup}>
                    <label htmlFor="startDateFilter" className={styles.dateLabel}>Başlangıç Tarihi:</label>
                    <input 
                      type="date" 
                      id="startDateFilter" 
                      name="startDateFilter"
                      value={startDateFilter}
                      onChange={(e) => setStartDateFilter(e.target.value)}
                      className={styles.dateInput}
                    />
                  </div>
                  <div className={styles.dateInputGroup}>
                    <label htmlFor="endDateFilter" className={styles.dateLabel}>Bitiş Tarihi:</label>
                    <input 
                      type="date" 
                      id="endDateFilter" 
                      name="endDateFilter"
                      value={endDateFilter}
                      onChange={(e) => setEndDateFilter(e.target.value)}
                      min={startDateFilter || ''} 
                      className={styles.dateInput}
                    />
                  </div>
                  {isAnyFilterActive && (
                      <button
                        className={styles.clearAllFiltersButton}
                        onClick={clearFilters}
                        title="Tüm Filtreleri Temizle"
                      >
                        <FaTimes /> Temizle
                      </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {selectedBudget && (
          <div className={styles.actionPanel}>
            <div className={styles.selectedBudgetInfo}>
              <h3 className={styles.selectedName}>
                {selectedBudget['BütçeAdı']}
                <span className={styles.selectedTypeBadge}>
                  ({selectedBudget['GiderTipi'] === 'Gelir' ? 'Gelir' : 'Gider'})
                </span>
              </h3>
              <span className={`${styles.statusBadge} ${
                 selectedBudget['ÖdemeDurumu'] === 'Ödendi' || selectedBudget['ÖdemeDurumu'] === 'Alındı' 
                  ? styles.statusPaid 
                  : selectedBudget['ÖdemeDurumu'] === 'Beklemede'
                    ? styles.statusPending
                    : styles.statusCancelled
              }`}>
                {selectedBudget['ÖdemeDurumu']}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
                <button 
                 className={`${styles.actionButton} ${styles.toggleButton}`}
                 onClick={handleToggleStatus}
                 disabled={!canToggleStatus}
                 title={canToggleStatus ? "Durumu Değiştir" : "Durum değiştirilemez"}
               >
                 <FaExchangeAlt className={styles.actionIcon} />
                 <span>Durumu Değiştir</span>
               </button>

              <button 
                className={`${styles.actionButton} ${styles.editButton}`}
                onClick={handleEditBudget}
              >
                <FaEdit className={styles.actionIcon} />
                <span>Düzenle</span>
              </button>
              <button 
                 className={`${styles.actionButton} ${styles.toggleActiveButton} ${isSelectedBudgetActive ? styles.makeInactive : styles.makeActive }`}
                 onClick={handleToggleActiveStatus}
                 title={isSelectedBudgetActive ? "Öğeyi pasif yap" : "Öğeyi aktif et"}
               >
                  {isSelectedBudgetActive ? 
                    <FaBan className={styles.actionIcon} /> : 
                    <FaCheckCircle className={styles.actionIcon} />
                  }
                  <span>{isSelectedBudgetActive ? "Pasif Yap" : "Aktif Et"}</span>
              </button>
              {isAdmin && (
                <button
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  onClick={handleDeleteBudget}
                  title="Kaydı Sil"
                >
                  <FaTrash className={styles.actionIcon} />
                  <span>Sil</span>
                </button>
              )}
            </div>

            {selectedBudget?.attachments?.length > 0 && (
              <div className={styles.attachmentsSection}>
                <h4 className={styles.attachmentsTitle}>
                  <FaPaperclip /> Ekler ({selectedBudget.attachments.length})
                </h4>
                <div className={styles.attachmentsList}>
                  {selectedBudget.attachments.map((file, index) => (
                    <a
                      key={index}
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.attachmentItem}
                      title={file.name}
                    >
                      <span className={styles.attachmentFileIcon}>{getAttachmentIcon(file.name)}</span>
                      <span className={styles.attachmentName}>{file.name}</span>
                      {file.size && (
                        <span className={styles.attachmentSize}>({(file.size / 1024).toFixed(1)} KB)</span>
                      )}
                      <FaExternalLinkAlt className={styles.attachmentOpenIcon} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {deleteAllConfirm && (
          <div className={styles.confirmOverlay}>
            <div className={styles.confirmDialog}>
              <h3 className={styles.confirmTitle}>Tüm Kayıtları Sil</h3>
              <p className={styles.confirmText}>
                Bütçe tablosundaki <strong>TÜM</strong> gelir ve gider kayıtları kalıcı olarak silinecek. Bu işlem geri alınamaz. Devam etmek istediğinize emin misiniz?
              </p>
              <div className={styles.confirmButtons}>
                <button
                  className={`${styles.button} ${styles.cancelButton}`}
                  onClick={() => setDeleteAllConfirm(false)}
                  disabled={isDeletingAll}
                >
                  Vazgeç
                </button>
                <button
                  className={`${styles.button} ${styles.confirmDeleteButton}`}
                  onClick={confirmDeleteAll}
                  disabled={isDeletingAll}
                >
                  <FaTrash /> {isDeletingAll ? 'Siliniyor...' : 'Evet, Tümünü Sil'}
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteConfirm && (
          <div className={styles.confirmOverlay}>
            <div className={styles.confirmDialog}>
              <h3 className={styles.confirmTitle}>Kaydı Sil</h3>
              <p className={styles.confirmText}>
                <strong>{selectedBudget?.['BütçeAdı']}</strong> kaydını kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              </p>
              <div className={styles.confirmButtons}>
                <button
                  className={`${styles.button} ${styles.cancelButton}`}
                  onClick={() => setDeleteConfirm(false)}
                >
                  Vazgeç
                </button>
                <button
                  className={`${styles.button} ${styles.confirmDeleteButton}`}
                  onClick={confirmDelete}
                >
                  <FaTrash /> Evet, Sil
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={styles.tabBar} role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'gelir'}
            className={`${styles.tabButton} ${activeTab === 'gelir' ? styles.tabButtonActive : ''}`}
            onClick={() => { setActiveTab('gelir'); setSelectedBudget(null); }}
          >
            Gelir Listesi
            <span className={styles.tabCount}>{filteredIncome.length}</span>
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'gider'}
            className={`${styles.tabButton} ${activeTab === 'gider' ? styles.tabButtonActive : ''}`}
            onClick={() => { setActiveTab('gider'); setSelectedBudget(null); }}
          >
            Gider Listesi
            <span className={styles.tabCount}>{filteredExpenses.length}</span>
          </button>
        </div>

        {activeTab === 'gelir' && (
        <div className={`${styles.tableWrapper} ${styles.incomeTable}`}>
          <div className={styles.tableHeader}>
            <h2 className={styles.tableTitle}>Gelir Listesi</h2>
          </div>

          <div className={styles.tableScrollContainer}>
             {showLeftScrollIncome && (
               <button
                 className={`${styles.scrollButton} ${styles.scrollLeft}`}
                 onClick={() => scrollTable('left', 'income')}
               >
                 <FaChevronLeft />
               </button>
             )}
             {showRightScrollIncome && (
               <button
                 className={`${styles.scrollButton} ${styles.scrollRight}`}
                 onClick={() => scrollTable('right', 'income')}
               >
                 <FaChevronRight />
               </button>
             )}
            <div
              ref={incomeTableContainerRef}
              className={styles.tableContainer}
              onScroll={() => handleScroll('income')}
            >
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>GİB Numarası<TableFilter column="GibNumarasi" type="text" onFilter={handleFilter} value={filters['GibNumarasi'] || ''} /></th>
                    <th>Taslak No<TableFilter column="TaslakNo" type="text" onFilter={handleFilter} value={filters['TaslakNo'] || ''} /></th>
                    <th>
                      Senaryo
                      <TableFilter column="Senaryo" type="select" options={[{ value: "", label: "Tümü" }, ...incomeUniqueValues.Senaryo]} onFilter={handleFilter} value={filters['Senaryo'] || ''} />
                    </th>
                    <th>
                      Fatura Tipi
                      <TableFilter column="FaturaTipi" type="select" options={[{ value: "", label: "Tümü" }, ...incomeUniqueValues.FaturaTipi]} onFilter={handleFilter} value={filters['FaturaTipi'] || ''} />
                    </th>
                    <th>Oluşturma Tarihi<TableFilter column="OlusturmaTarihi" type="date" onFilter={handleFilter} value={filters['OlusturmaTarihi'] || ''} /></th>
                    <th>Oluşturma Zamanı<TableFilter column="OlusturmaZamani" type="text" onFilter={handleFilter} value={filters['OlusturmaZamani'] || ''} /></th>
                    <th>Belge Tarihi<TableFilter column="BelgeTarihi" type="date" onFilter={handleFilter} value={filters['BelgeTarihi'] || ''} /></th>
                    <th>Belge Zamanı<TableFilter column="BelgeZamani" type="text" onFilter={handleFilter} value={filters['BelgeZamani'] || ''} /></th>
                    <th>Müşteri TCKN/VKN<TableFilter column="MusteriTcknVkn" type="text" onFilter={handleFilter} value={filters['MusteriTcknVkn'] || ''} /></th>
                    <th>
                      Müşteri Unvan
                      <TableFilter column="MusteriUnvan" type="select" options={[{ value: "", label: "Tümü" }, ...incomeUniqueValues.MusteriUnvan]} onFilter={handleFilter} value={filters['MusteriUnvan'] || ''} />
                    </th>
                    <th>Müşteri Etiketi<TableFilter column="MusteriEtiketi" type="text" onFilter={handleFilter} value={filters['MusteriEtiketi'] || ''} /></th>
                    <th>Tutar<TableFilter column="Tutar" type="text" onFilter={handleFilter} value={filters['Tutar'] || ''} /></th>
                    <th>
                      Para Birimi
                      <TableFilter column="ParaBirimi" type="select" options={[{ value: "", label: "Tümü" }, ...incomeUniqueValues.ParaBirimi]} onFilter={handleFilter} value={filters['ParaBirimi'] || ''} />
                    </th>
                    <th>Zarf UUID<TableFilter column="ZarfUuid" type="text" onFilter={handleFilter} value={filters['ZarfUuid'] || ''} /></th>
                    <th>
                      Zarf Durumu
                      <TableFilter column="ZarfDurumu" type="select" options={[{ value: "", label: "Tümü" }, ...incomeUniqueValues.ZarfDurumu]} onFilter={handleFilter} value={filters['ZarfDurumu'] || ''} />
                    </th>
                    <th>ETTN<TableFilter column="Ettn" type="text" onFilter={handleFilter} value={filters['Ettn'] || ''} /></th>
                    <th>İskonto<TableFilter column="Iskonto" type="text" onFilter={handleFilter} value={filters['Iskonto'] || ''} /></th>
                    <th>Fatura Dışı Masraflar<TableFilter column="FaturaDisiMasraflari" type="text" onFilter={handleFilter} value={filters['FaturaDisiMasraflari'] || ''} /></th>
                    <th>Satır Sayısı<TableFilter column="SatirSayisi" type="text" onFilter={handleFilter} value={filters['SatirSayisi'] || ''} /></th>
                    <th>Birim Fiyat * Miktar Tutarı<TableFilter column="BirimFiyatMiktarTutari" type="text" onFilter={handleFilter} value={filters['BirimFiyatMiktarTutari'] || ''} /></th>
                    <th>Ödenecek Tutar<TableFilter column="OdenecekTutar" type="text" onFilter={handleFilter} value={filters['OdenecekTutar'] || ''} /></th>
                    <th>Vergiler Hariç Tutar<TableFilter column="VergilerHaricTutar" type="text" onFilter={handleFilter} value={filters['VergilerHaricTutar'] || ''} /></th>
                    <th>Vergiler Dahil Tutar<TableFilter column="VergilerDahilTutar" type="text" onFilter={handleFilter} value={filters['VergilerDahilTutar'] || ''} /></th>
                    <th>
                      Ödeme Durumu
                      <TableFilter column="ÖdemeDurumu" type="select" options={[{ value: "", label: "Tümü" }, ...incomeUniqueValues.ÖdemeDurumu]} onFilter={handleFilter} value={filters['ÖdemeDurumu'] || ''} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIncome.length > 0 ? (
                    filteredIncome.map((income) => (
                      <tr
                        key={income.id}
                        onClick={() => handleBudgetSelect(income)}
                        className={`${selectedBudget?.id === income.id ? styles.selectedRow : ''} ${income.AktifDurum === 'Pasif' ? styles.inactiveRow : ''}`}
                      >
                        <td>{truncateText(pick(income, 'GibNumarasi'), 18) || '-'}</td>
                        <td>{pick(income, 'TaslakNo') || '-'}</td>
                        <td>{pick(income, 'Senaryo') || '-'}</td>
                        <td>{pick(income, 'FaturaTipi') || '-'}</td>
                        <td>{pick(income, 'OlusturmaTarihi') || '-'}</td>
                        <td>{pick(income, 'OlusturmaZamani') || '-'}</td>
                        <td>{pick(income, 'BelgeTarihi', 'BütçeTarihi') || '-'}</td>
                        <td>{pick(income, 'BelgeZamani') || '-'}</td>
                        <td>{pick(income, 'MusteriTcknVkn') || '-'}</td>
                        <td title={pick(income, 'MusteriUnvan', 'BütçeKaynak')}>{truncateText(pick(income, 'MusteriUnvan', 'BütçeKaynak') || getEmployeeName(income['BütçeKaynak']), 35)}</td>
                        <td>{pick(income, 'MusteriEtiketi') || '-'}</td>
                        <td>{fmtAmount(pick(income, 'Tutar', 'BütçeMiktarı'))}</td>
                        <td>{income['ParaBirimi'] || '-'}</td>
                        <td title={pick(income, 'ZarfUuid')}>{truncateText(pick(income, 'ZarfUuid'), 14) || '-'}</td>
                        <td>{pick(income, 'ZarfDurumu') || '-'}</td>
                        <td title={pick(income, 'Ettn')}>{truncateText(pick(income, 'Ettn'), 14) || '-'}</td>
                        <td>{fmtAmount(income['Iskonto'])}</td>
                        <td>{fmtAmount(income['FaturaDisiMasraflari'])}</td>
                        <td>{fmtInt(income['SatirSayisi'])}</td>
                        <td>{fmtAmount(income['BirimFiyatMiktarTutari'])}</td>
                        <td>{fmtAmount(income['OdenecekTutar'])}</td>
                        <td>{fmtAmount(income['VergilerHaricTutar'])}</td>
                        <td>{fmtAmount(income['VergilerDahilTutar'])}</td>
                        <td>
                           <span className={`${styles.statusBadge} ${
                              income['ÖdemeDurumu'] === 'Alındı' ? styles.statusPaid :
                              income['ÖdemeDurumu'] === 'Beklemede' ? styles.statusPending :
                              styles.statusCancelled
                           }`}>
                             {income['ÖdemeDurumu']}
                           </span>
                         </td>
                       </tr>
                     ))
                   ) : (
                     <tr>
                       <td colSpan="24" className={styles.noDataCell}>
                         <div className={styles.noData}>
                           <FaWallet className={styles.noDataIcon} />
                           <p>
                             {isAnyFilterActive ? 'Filtrelere uygun gelir bulunamadı' : 'Henüz bir gelir kaydı yok'}
                           </p>
                           {isAnyFilterActive && (
                <button
                  className={styles.clearFiltersButton}
                  onClick={clearFilters}
                >
                              Filtreleri Temizle
                </button>
              )}
            </div>
                       </td>
                     </tr>
                   )}
                 </tbody>
               </table>
             </div>
           </div>
         </div>

        )}

        {activeTab === 'gider' && (
        <div className={`${styles.tableWrapper} ${styles.expenseTable}`}>
          <div className={styles.tableHeader}>
            <h2 className={styles.tableTitle}>Gider Listesi</h2>
          </div>

          <div className={styles.tableScrollContainer}>
             {showLeftScrollExpense && (
              <button
                className={`${styles.scrollButton} ${styles.scrollLeft}`}
                 onClick={() => scrollTable('left', 'expense')}
              >
                <FaChevronLeft />
              </button>
            )}
             {showRightScrollExpense && (
              <button
                className={`${styles.scrollButton} ${styles.scrollRight}`}
                 onClick={() => scrollTable('right', 'expense')}
              >
                <FaChevronRight />
              </button>
            )}
            <div
              ref={expenseTableContainerRef}
              className={styles.tableContainer}
              onScroll={() => handleScroll('expense')}
            >
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>GİB Numarası<TableFilter column="GibNumarasi" type="text" onFilter={handleFilter} value={filters['GibNumarasi'] || ''} /></th>
                    <th>Taslak No<TableFilter column="TaslakNo" type="text" onFilter={handleFilter} value={filters['TaslakNo'] || ''} /></th>
                    <th>
                      Senaryo
                      <TableFilter column="Senaryo" type="select" options={[{ value: "", label: "Tümü" }, ...expenseUniqueValues.Senaryo]} onFilter={handleFilter} value={filters['Senaryo'] || ''} />
                    </th>
                    <th>
                      Fatura Tipi
                      <TableFilter column="FaturaTipi" type="select" options={[{ value: "", label: "Tümü" }, ...expenseUniqueValues.FaturaTipi]} onFilter={handleFilter} value={filters['FaturaTipi'] || ''} />
                    </th>
                    <th>Oluşturma Tarihi<TableFilter column="OlusturmaTarihi" type="date" onFilter={handleFilter} value={filters['OlusturmaTarihi'] || ''} /></th>
                    <th>Oluşturma Zamanı<TableFilter column="OlusturmaZamani" type="text" onFilter={handleFilter} value={filters['OlusturmaZamani'] || ''} /></th>
                    <th>Belge Tarihi<TableFilter column="BelgeTarihi" type="date" onFilter={handleFilter} value={filters['BelgeTarihi'] || ''} /></th>
                    <th>Belge Zamanı<TableFilter column="BelgeZamani" type="text" onFilter={handleFilter} value={filters['BelgeZamani'] || ''} /></th>
                    <th>Gönderici TCKN/VKN<TableFilter column="GondericiTcknVkn" type="text" onFilter={handleFilter} value={filters['GondericiTcknVkn'] || ''} /></th>
                    <th>
                      Unvan
                      <TableFilter column="GondericiUnvan" type="select" options={[{ value: "", label: "Tümü" }, ...expenseUniqueValues.GondericiUnvan]} onFilter={handleFilter} value={filters['GondericiUnvan'] || ''} />
                    </th>
                    <th>Gönderici Etiketi<TableFilter column="GondericiEtiketi" type="text" onFilter={handleFilter} value={filters['GondericiEtiketi'] || ''} /></th>
                    <th>Tutar<TableFilter column="Tutar" type="text" onFilter={handleFilter} value={filters['Tutar'] || ''} /></th>
                    <th>
                      Para Birimi
                      <TableFilter column="ParaBirimi" type="select" options={[{ value: "", label: "Tümü" }, ...expenseUniqueValues.ParaBirimi]} onFilter={handleFilter} value={filters['ParaBirimi'] || ''} />
                    </th>
                    <th>Zarf UUID<TableFilter column="ZarfUuid" type="text" onFilter={handleFilter} value={filters['ZarfUuid'] || ''} /></th>
                    <th>
                      Zarf Durumu
                      <TableFilter column="ZarfDurumu" type="select" options={[{ value: "", label: "Tümü" }, ...expenseUniqueValues.ZarfDurumu]} onFilter={handleFilter} value={filters['ZarfDurumu'] || ''} />
                    </th>
                    <th>ETTN<TableFilter column="Ettn" type="text" onFilter={handleFilter} value={filters['Ettn'] || ''} /></th>
                    <th>İskonto<TableFilter column="Iskonto" type="text" onFilter={handleFilter} value={filters['Iskonto'] || ''} /></th>
                    <th>Fatura Dışı Masraflar<TableFilter column="FaturaDisiMasraflari" type="text" onFilter={handleFilter} value={filters['FaturaDisiMasraflari'] || ''} /></th>
                    <th>Satır Sayısı<TableFilter column="SatirSayisi" type="text" onFilter={handleFilter} value={filters['SatirSayisi'] || ''} /></th>
                    <th>Birim Fiyat * Miktar Tutarı<TableFilter column="BirimFiyatMiktarTutari" type="text" onFilter={handleFilter} value={filters['BirimFiyatMiktarTutari'] || ''} /></th>
                    <th>Ödenecek Tutar<TableFilter column="OdenecekTutar" type="text" onFilter={handleFilter} value={filters['OdenecekTutar'] || ''} /></th>
                    <th>Vergiler Hariç Tutar<TableFilter column="VergilerHaricTutar" type="text" onFilter={handleFilter} value={filters['VergilerHaricTutar'] || ''} /></th>
                    <th>Vergiler Dahil Tutar<TableFilter column="VergilerDahilTutar" type="text" onFilter={handleFilter} value={filters['VergilerDahilTutar'] || ''} /></th>
                    <th>
                      Ödeme Durumu
                      <TableFilter column="ÖdemeDurumu" type="select" options={[{ value: "", label: "Tümü" }, ...expenseUniqueValues.ÖdemeDurumu]} onFilter={handleFilter} value={filters['ÖdemeDurumu'] || ''} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.length > 0 ? (
                    filteredExpenses.map((expense) => (
                      <tr
                        key={expense.id}
                        onClick={() => handleBudgetSelect(expense)}
                        className={`${selectedBudget?.id === expense.id ? styles.selectedRow : ''} ${expense.AktifDurum === 'Pasif' ? styles.inactiveRow : ''}`}
                      >
                        <td>{truncateText(pick(expense, 'GibNumarasi'), 18) || '-'}</td>
                        <td>{pick(expense, 'TaslakNo') || '-'}</td>
                        <td>{pick(expense, 'Senaryo') || '-'}</td>
                        <td>{pick(expense, 'FaturaTipi') || '-'}</td>
                        <td>{pick(expense, 'OlusturmaTarihi') || '-'}</td>
                        <td>{pick(expense, 'OlusturmaZamani') || '-'}</td>
                        <td>{pick(expense, 'BelgeTarihi', 'BütçeTarihi') || '-'}</td>
                        <td>{pick(expense, 'BelgeZamani') || '-'}</td>
                        <td>{pick(expense, 'GondericiTcknVkn') || '-'}</td>
                        <td title={pick(expense, 'GondericiUnvan', 'BütçeKaynak')}>{truncateText(pick(expense, 'GondericiUnvan', 'BütçeKaynak') || getEmployeeName(expense['BütçeKaynak']), 35)}</td>
                        <td>{pick(expense, 'GondericiEtiketi') || '-'}</td>
                        <td>{fmtAmount(pick(expense, 'Tutar', 'BütçeMiktarı'))}</td>
                        <td>{expense['ParaBirimi'] || '-'}</td>
                        <td title={pick(expense, 'ZarfUuid')}>{truncateText(pick(expense, 'ZarfUuid'), 14) || '-'}</td>
                        <td>{pick(expense, 'ZarfDurumu') || '-'}</td>
                        <td title={pick(expense, 'Ettn')}>{truncateText(pick(expense, 'Ettn'), 14) || '-'}</td>
                        <td>{fmtAmount(expense['Iskonto'])}</td>
                        <td>{fmtAmount(expense['FaturaDisiMasraflari'])}</td>
                        <td>{fmtInt(expense['SatirSayisi'])}</td>
                        <td>{fmtAmount(expense['BirimFiyatMiktarTutari'])}</td>
                        <td>{fmtAmount(expense['OdenecekTutar'])}</td>
                        <td>{fmtAmount(expense['VergilerHaricTutar'])}</td>
                        <td>{fmtAmount(expense['VergilerDahilTutar'])}</td>
                        <td>
                          <span className={`${styles.statusBadge} ${
                             expense['ÖdemeDurumu'] === 'Ödendi' ? styles.statusPaid :
                             expense['ÖdemeDurumu'] === 'Beklemede' ? styles.statusPending :
                             styles.statusCancelled
                           }`}>
                             {expense['ÖdemeDurumu']}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                       <td colSpan="24" className={styles.noDataCell}>
                        <div className={styles.noData}>
                          <FaWallet className={styles.noDataIcon} />
                          <p>
                             {isAnyFilterActive ? 'Filtrelere uygun gider bulunamadı' : 'Henüz bir gider kaydı yok'}
                          </p>
                           {isAnyFilterActive && (
                            <button 
                              className={styles.clearFiltersButton} 
                              onClick={clearFilters}
                            >
                              Filtreleri Temizle
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        )}

        <StatisticsSection budgets={budgets} isDarkMode={isDarkMode} />

      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />

      {isAddIncomeModalOpen && (
        <AddIncomeModal
          isOpen={isAddIncomeModalOpen}
          onClose={() => setIsAddIncomeModalOpen(false)}
          onSubmit={handleAddItem}
        />
      )}
      {isAddExpenseModalOpen && (
        <AddExpenseModal
          isOpen={isAddExpenseModalOpen}
          onClose={() => setIsAddExpenseModalOpen(false)}
          onSubmit={handleAddItem}
        />
      )}

      {isImportIncomeModalOpen && (
        <ImportIncomeExcelModal
          isOpen={isImportIncomeModalOpen}
          onClose={() => setIsImportIncomeModalOpen(false)}
          onImported={fetchBudgets}
        />
      )}

      {isImportExpenseModalOpen && (
        <ImportExpenseExcelModal
          isOpen={isImportExpenseModalOpen}
          onClose={() => setIsImportExpenseModalOpen(false)}
          onImported={fetchBudgets}
        />
      )}

      {isEditIncomeModalOpen && editingBudget && (
        <EditIncomeModal
          isOpen={isEditIncomeModalOpen}
          onClose={() => { setIsEditIncomeModalOpen(false); setEditingBudget(null); }}
          onSubmit={handleUpdateBudget}
          budgetData={editingBudget}
        />
      )}
      {isEditExpenseModalOpen && editingBudget && (
        <EditExpenseModal
          isOpen={isEditExpenseModalOpen}
          onClose={() => { setIsEditExpenseModalOpen(false); setEditingBudget(null); }}
          onSubmit={handleUpdateBudget}
          budgetData={editingBudget}
        />
      )}
    </div>
  );
};

export default BudgetManagementPage;
